import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useArtistContext } from '../core/ArtistContext';
import { getHeadshotNavMembership } from './getHeadshotNavMembership';
import { useHeadshotAnnualModal } from './useHeadshotAnnualModal';
import { useHeadshotBookedModal } from './useHeadshotBookedModal';
import { HeadshotsModalName, useHeadshotEligibility } from './useHeadshotEligibility';
import { useHeadshotFreeModal } from './useHeadshotFreeModal';
import { useHeadshotMonthlyModal } from './useHeadshotMonthlyModal';
import { useHeadshotTrialModal } from './useHeadshotTrialModal';

export type { HeadshotsModalName };

/**
 * Owns the modal opened from the Headshots nav link. Only one modal is open at a
 * time. A click opens `headshotModal` from `useHeadshotEligibility` instead of
 * navigating; otherwise the link navigates to the Headshots page.
 */
export const useHeadshotModal = () => {
  const [activeModal, setActiveModal] = useState<HeadshotsModalName | null>(null);
  const { headshotEligibility, isFreeHeadshotsSubscriber, isPaidTrialSubscriber } = useArtistContext();
  const closeModal = useCallback(() => setActiveModal(null), []);
  const { headshotModal } = useHeadshotEligibility();
  const { trialEndDate, handleTrialUpgrade } = useHeadshotTrialModal({ onClose: closeModal });
  const { sessionDateTime } = useHeadshotBookedModal();
  const { nextSessionDate, handleUploadMedia } = useHeadshotAnnualModal({ onClose: closeModal });
  const { nextBookableDate, handleMonthlyUpgrade } = useHeadshotMonthlyModal({ onClose: closeModal });
  const { handleFreeUpgrade } = useHeadshotFreeModal({ onClose: closeModal });

  const handleHeadshotsClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const membership = getHeadshotNavMembership({
        isFreeHeadshotsSubscriber: Boolean(isFreeHeadshotsSubscriber),
        isPaidTrialSubscriber: Boolean(isPaidTrialSubscriber),
        planTermCode: headshotEligibility?.planTermCode,
      });

      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_CLICK_NAV_HEADSHOTS,
        data: membership ? { membership } : undefined,
      });

      if (!headshotModal) {
        return;
      }

      event.preventDefault();
      setActiveModal(headshotModal);
    },
    [headshotEligibility?.planTermCode, headshotModal, isFreeHeadshotsSubscriber, isPaidTrialSubscriber]
  );

  return useMemo(
    () => ({
      activeModal,
      closeModal,
      handleHeadshotsClick,
      trialEndDate,
      handleTrialUpgrade,
      sessionDateTime,
      nextSessionDate,
      handleUploadMedia,
      nextBookableDate,
      handleMonthlyUpgrade,
      handleFreeUpgrade,
    }),
    [
      activeModal,
      closeModal,
      handleFreeUpgrade,
      handleHeadshotsClick,
      handleMonthlyUpgrade,
      handleTrialUpgrade,
      handleUploadMedia,
      nextBookableDate,
      nextSessionDate,
      sessionDateTime,
      trialEndDate,
    ]
  );
};

export type UseHeadshotModalValues = ReturnType<typeof useHeadshotModal>;
